import { SCORER_API_URL } from '@/utils/scorer-request.js';
import { getSubsystemToken } from '@/utils/subsystem-token';

const MAX_RECONNECT_DELAY = 30000;

const state = {
	matchId: null,
	matchType: null,
	matchMode: null,
	playerCount: 2,
	players: [],
	currentFrame: 1,
	status: 0,
	winner: null,
	totalFrames: null,
	timeLimit: null,
	tableFeePool: 0,
	roomCode: null,
	wsConnected: false,
	wsClient: null,
	_reconnectTimer: null,
	_reconnectDelay: 1000,
	_shouldReconnect: false
};

const mutations = {
	SET_MATCH(state, match) {
		if (match.matchId !== undefined) state.matchId = match.matchId;
		if (match.matchType !== undefined) state.matchType = match.matchType;
		if (match.matchMode !== undefined) state.matchMode = match.matchMode;
		if (match.playerCount !== undefined) state.playerCount = match.playerCount;
		if (match.players !== undefined) state.players = match.players;
		if (match.currentFrame !== undefined) state.currentFrame = match.currentFrame;
		if (match.status !== undefined) state.status = match.status;
		if (match.winner !== undefined) state.winner = match.winner;
		if (match.totalFrames !== undefined) state.totalFrames = match.totalFrames;
		if (match.timeLimit !== undefined) state.timeLimit = match.timeLimit;
		if (match.tableFeePool !== undefined) state.tableFeePool = match.tableFeePool;
	},
	SET_REALTIME_STATE(state, data) {
		if (data.matchId !== undefined) state.matchId = data.matchId;
		if (data.matchType !== undefined) state.matchType = data.matchType;
		if (data.matchMode !== undefined) state.matchMode = data.matchMode;
		if (data.playerCount !== undefined) state.playerCount = data.playerCount;
		if (data.players !== undefined) state.players = data.players;
		if (data.currentFrame !== undefined) state.currentFrame = data.currentFrame;
		if (data.status !== undefined) state.status = data.status;
		if (data.winner !== undefined) state.winner = data.winner;
		if (data.totalFrames !== undefined) state.totalFrames = data.totalFrames;
		if (data.timeLimit !== undefined) state.timeLimit = data.timeLimit;
		if (data.tableFeePool !== undefined) state.tableFeePool = data.tableFeePool;
	},
	SET_ROOM_CODE(state, code) {
		state.roomCode = code;
	},
	SET_WS_CONNECTED(state, val) {
		state.wsConnected = val;
	},
	SET_WS_CLIENT(state, client) {
		state.wsClient = client;
	},
	RESET_MATCH(state) {
		state.matchId = null;
		state.matchType = null;
		state.matchMode = null;
		state.playerCount = 2;
		state.players = [];
		state.currentFrame = 1;
		state.status = 0;
		state.winner = null;
		state.totalFrames = null;
		state.timeLimit = null;
		state.tableFeePool = 0;
		state.roomCode = null;
		state.wsConnected = false;
		state.wsClient = null;
		state._shouldReconnect = false;
		if (state._reconnectTimer) {
			clearTimeout(state._reconnectTimer);
			state._reconnectTimer = null;
		}
		state._reconnectDelay = 1000;
	}
};

const getters = {
	getPlayer: (state) => (index) => {
		return state.players.find(p => p.playerIndex === index) || null;
	}
};

const actions = {
	connectWebSocket({ commit, state, dispatch }, roomCode) {
		state._shouldReconnect = true;
		state._reconnectDelay = 1000;
		doConnect({ commit, state, dispatch }, roomCode);
	},
	disconnectWebSocket({ commit, state }) {
		state._shouldReconnect = false;
		if (state._reconnectTimer) {
			clearTimeout(state._reconnectTimer);
			state._reconnectTimer = null;
		}
		if (state.wsClient) {
			state.wsClient.close();
		}
		commit('SET_WS_CONNECTED', false);
		commit('SET_WS_CLIENT', null);
	}
};

function doConnect(ctx, roomCode) {
	getSubsystemToken('scorer').then((token) => {
		openSocket(ctx, roomCode, token);
	}).catch(() => {
		openSocket(ctx, roomCode, null);
	});
}

function openSocket({ commit, state, dispatch }, roomCode, token) {
	const wsUrl = SCORER_API_URL.replace('http', 'ws') + '/ws/scorer';
	const socketTask = uni.connectSocket({
		url: wsUrl,
		header: { 'X-Room-Code': roomCode },
		success: () => {}
	});
	socketTask.onOpen(() => {
		commit('SET_WS_CONNECTED', true);
		commit('SET_WS_CLIENT', socketTask);
		state._reconnectDelay = 1000;
		const authLine = token ? ('Authorization:Bearer ' + token + '\n') : '';
		const connectFrame = 'CONNECT\naccept-version:1.1,1.0\n' + authLine + 'X-Room-Code:' + roomCode + '\n\n\0';
		socketTask.send({ data: connectFrame });
		setTimeout(() => {
			const subFrame = 'SUBSCRIBE\nid:sub-0\ndestination:/topic/scorer/room/' + roomCode + '\n\n\0';
			socketTask.send({ data: subFrame });
		}, 500);
	});
	socketTask.onMessage((res) => {
		if (res.data && res.data.indexOf('{') >= 0) {
			const jsonStr = res.data.substring(res.data.indexOf('{'), res.data.lastIndexOf('}') + 1);
			try {
				const data = JSON.parse(jsonStr);
				commit('SET_REALTIME_STATE', data);
			} catch (e) {}
		}
	});
	socketTask.onClose(() => {
		commit('SET_WS_CONNECTED', false);
		commit('SET_WS_CLIENT', null);
		if (state._shouldReconnect) {
			state._reconnectTimer = setTimeout(() => {
				doConnect({ commit, state, dispatch }, roomCode);
			}, state._reconnectDelay);
			state._reconnectDelay = Math.min(state._reconnectDelay * 2, MAX_RECONNECT_DELAY);
		}
	});
	socketTask.onError(() => {
		commit('SET_WS_CONNECTED', false);
	});
}

export default {
	namespaced: true,
	state,
	getters,
	mutations,
	actions
};
